import React, { useEffect, useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td, Box, Spinner, Flex, IconButton, Tooltip, Text, Input, useToast } from "@chakra-ui/react";
import { FaTrash, FaPen, FaCheck, FaTimes } from "react-icons/fa";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

const DuplicateStores = ({ onCountChange }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRow, setEditingRow] = useState(null); // { gi, di }
  const [editValue, setEditValue] = useState("");
  const toast = useToast();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/validation/duplicate-stores`, {});
      const rows = res.data?.data || [];
      setData(rows);
      if (onCountChange) onCountChange(rows.length);
    } catch (err) {
      toast({ title: "Failed to load duplicates", status: "error", duration: 3000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (siteCode, siteDescription, retailer) => {
    try {
      await axios.post(`${API}/validation/duplicate-stores/delete`, { site_code: siteCode, site_description: siteDescription, retailer });
      toast({ title: "Deleted", description: `${siteCode} — ${siteDescription}`, status: "success", duration: 3000, isClosable: true });
      setEditingRow(null);
      fetchData();
    } catch (err) {
      toast({ title: "Delete failed", description: err?.response?.data?.message || "Error", status: "error", duration: 3000, isClosable: true });
    }
  };

  const startEdit = (gi, di, currentCode) => {
    setEditingRow({ gi, di });
    setEditValue(currentCode);
  };

  const cancelEdit = () => {
    setEditingRow(null);
    setEditValue("");
  };

  const saveEdit = async (group, di) => {
    const newCode = editValue.trim();
    if (!newCode || newCode === group.site_code) {
      cancelEdit();
      return;
    }
    try {
      await axios.post(`${API}/validation/duplicate-stores/update-sitecode`, {
        old_site_code: group.site_code,
        new_site_code: newCode,
        site_description: group.descriptions[di],
        retailer: group.retailer,
        region: group.regions[di],
      });
      toast({ title: "Site code updated", description: `${group.site_code} → ${newCode}`, status: "success", duration: 3000, isClosable: true });
      setEditingRow(null);
      fetchData();
    } catch (err) {
      toast({ title: "Update failed", description: err?.response?.data?.message || "Error", status: "error", duration: 3000, isClosable: true });
    }
  };

  if (loading) return <Flex justify="center" p={4}><Spinner size="md" /></Flex>;
  if (data.length === 0) return <Text fontSize="sm" color="gray.500">No duplicate store entries found.</Text>;

  return (
    <Box maxH="500px" overflowY="auto">
      <Table size="sm" variant="simple" border="1px" borderColor="gray.200">
        <Thead>
          <Tr bg="gray.50">
            <Th fontSize="xs">Site Code</Th>
            <Th fontSize="xs">Retailer</Th>
            <Th fontSize="xs">Description</Th>
            <Th fontSize="xs">Region</Th>
            <Th fontSize="xs" w="80px">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((group, gi) =>
            group.descriptions.map((desc, di) => {
              const isEditing = editingRow?.gi === gi && editingRow?.di === di;
              return (
                <Tr key={`${gi}-${di}`} bg={di === 0 ? "white" : "orange.50"}>
                  {di === 0 ? (
                    <Td fontSize="xs" fontWeight="600" rowSpan={group.descriptions.length}>{group.site_code}</Td>
                  ) : null}
                  {di === 0 ? (
                    <Td fontSize="xs" rowSpan={group.descriptions.length}>{group.retailer}</Td>
                  ) : null}
                  <Td fontSize="xs">{desc}</Td>
                  <Td fontSize="xs">{group.regions[di]}</Td>
                  <Td>
                    {isEditing ? (
                      <Flex align="center" gap={1}>
                        <Input size="xs" value={editValue} onChange={(e) => setEditValue(e.target.value)} w="80px"
                          onKeyDown={(e) => { if (e.key === "Enter") saveEdit(group, di); if (e.key === "Escape") cancelEdit(); }}
                          autoFocus
                        />
                        <IconButton icon={<FaCheck />} size="xs" colorScheme="green" variant="ghost" onClick={() => saveEdit(group, di)} aria-label="Save" />
                        <IconButton icon={<FaTimes />} size="xs" variant="ghost" onClick={cancelEdit} aria-label="Cancel" />
                      </Flex>
                    ) : (
                      <Flex gap={1}>
                        <Tooltip label="Change site_code for this entry">
                          <IconButton icon={<FaPen />} size="xs" colorScheme="blue" variant="ghost" onClick={() => startEdit(gi, di, group.site_code)} aria-label="Edit" />
                        </Tooltip>
                        <Tooltip label={`Delete "${desc}"`}>
                          <IconButton icon={<FaTrash />} size="xs" colorScheme="red" variant="ghost" onClick={() => handleDelete(group.site_code, desc, group.retailer)} aria-label="Delete" />
                        </Tooltip>
                      </Flex>
                    )}
                  </Td>
                </Tr>
              );
            })
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

export default DuplicateStores;
